import dayjs from "dayjs";
// Registered here rather than relying on App.js having loaded first: a util must
// localise correctly for any caller, including a test that imports it alone.
import "dayjs/locale/id";

export const addMinutes = (date, minutes) =>
  new Date(date.getTime() + minutes * 60000);

export const dayStart = (ref) =>
  new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), 0, 0, 0, 0);

export const dayEnd = (ref) =>
  new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), 23, 45, 0, 0);

/**
 * The calendar day of `date` carrying the clock time of `reference`.
 *
 * A date picker without a time column hands back local midnight, so every entry
 * made on the same day serialises to the identical instant and nothing can order
 * them. Stamping the current clock onto the picked day gives each one a distinct,
 * truthful timestamp. Seconds and milliseconds ride along on purpose: they break
 * ties between two entries saved within the same minute.
 *
 * Neither argument is mutated, and an unparseable `date` is returned untouched so
 * a caller can pass a picker's value straight through without guarding first.
 */
export const withTimeOfDay = (date, reference = new Date()) => {
  // Guarded before the Date() call, which turns null into the epoch rather than
  // into something invalid — a cleared field must stay cleared.
  if (!date) return date;

  const day = date instanceof Date ? date : new Date(date);
  if (!(day instanceof Date) || isNaN(day.getTime())) return date;

  const ref =
    reference instanceof Date && !isNaN(reference.getTime())
      ? reference
      : new Date();

  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    ref.getHours(),
    ref.getMinutes(),
    ref.getSeconds(),
    ref.getMilliseconds(),
  );
};

// Exactly 00:00:00.000 local — what a calendar cell yields on its own, as opposed
// to a time somebody actually chose.
const isLocalMidnight = (value) =>
  value instanceof Date &&
  !isNaN(value.getTime()) &&
  value.getHours() === 0 &&
  value.getMinutes() === 0 &&
  value.getSeconds() === 0 &&
  value.getMilliseconds() === 0;

/**
 * What a date picker's new value should actually become.
 *
 * A picker with a time field emits through one `onChange` whether the user
 * clicked a day or typed a time, so the three cases have to be told apart by
 * what arrives:
 *
 *   nothing selected yet + midnight -> a bare day click; stamp the current clock,
 *                                      otherwise every entry made today shares one
 *                                      instant and the ledger cannot be ordered
 *   nothing selected yet + a time   -> the time field was used first; that time is
 *                                      the user's and is left alone
 *   something already selected      -> react-datepicker carries the established
 *                                      time across day changes, and the time field
 *                                      must be able to change it; never restamp
 *
 * `now` is injectable so the behaviour can be pinned in a test.
 */
export const resolvePickedDateTime = (picked, previous, now = new Date()) => {
  if (!picked || previous) return picked;

  return isLocalMidnight(picked) ? withTimeOfDay(picked, now) : picked;
};

/**
 * "24 Agt 2026 · 22:34" — one accounting record's date with its clock time.
 *
 * The separator is a middle dot to match the mobile app, which already renders
 * every accounting and cash book timestamp this way. Two products reading the
 * same ledger should spell an instant identically, so a figure quoted from one
 * is recognisable in the other.
 *
 * Use this wherever a timestamp sits on a single line. Table cells use
 * DateTimeCell instead, which stacks the time under the date to keep the column
 * narrow — same information, and a row has the vertical space to spend.
 *
 * Note this is for accounting timestamps. Consultation, webinar and forum
 * screens carry their own older formats and are deliberately left alone.
 */
export const formatDateTime = (value, locale = "id") => {
  if (!value) return "-";

  const d = dayjs(value);
  return d.isValid() ? d.locale(locale).format("D MMM YYYY · HH:mm") : "-";
};

const resolveRef = (a, b) => a || b || new Date();

export const getWorkStartBounds = (
  startTime,
  endTime,
  intervalMinutes = 15
) => {
  const ref = resolveRef(startTime, endTime);
  const minTime = dayStart(ref);
  const maxTime = endTime ? addMinutes(endTime, -intervalMinutes) : dayEnd(ref);
  return { minTime, maxTime };
};

export const getWorkEndBounds = (startTime, endTime, intervalMinutes = 15) => {
  const ref = resolveRef(endTime, startTime);
  const minTime = startTime
    ? addMinutes(startTime, intervalMinutes)
    : dayStart(ref);
  const maxTime = dayEnd(ref);
  return { minTime, maxTime };
};

export const DEFAULT_TIMEZONE = 'Asia/Jakarta';

const DEFAULT_START_TIMES = [9, 9, 9, 9, 9, 9, 9];
const DEFAULT_END_TIMES = [17, 17, 17, 17, 17, 17, 17];
const DEFAULT_ACTIVE_DAYS = [false, true, true, true, true, true, false];

const resolveTimezone = (timezone) =>
  timezone && typeof timezone === 'string' ? timezone : DEFAULT_TIMEZONE;

const zonedFormatter = (timeZone) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

// Every date component has to come from the same timezone. Reading the weekday
// from the browser while reading the hour from the target zone puts the
// schedule on the wrong day for anyone either side of midnight.
export const getZonedParts = (date = new Date(), timezone = DEFAULT_TIMEZONE) => {
  let parts;
  try {
    parts = zonedFormatter(resolveTimezone(timezone)).formatToParts(date);
  } catch (error) {
    parts = zonedFormatter(DEFAULT_TIMEZONE).formatToParts(date);
  }

  const read = (type) =>
    parseInt(parts.find((p) => p.type === type)?.value, 10);

  const year = read('year');
  const month = read('month');
  const day = read('day');

  return {
    year,
    month,
    day,
    // hour12:false reports midnight as 24 on some engines.
    hour: read('hour') % 24,
    minute: read('minute'),
    second: read('second'),
    // Derived from the zoned calendar date, so it always matches the hour.
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  };
};

// Converts a wall-clock time in `timezone` into the real instant it refers to.
// Runs the correction twice so a DST transition between the guess and the
// target does not shift the result.
const zonedWallClockToDate = (
  { year, month, day, hour = 0, minute = 0 },
  timezone
) => {
  let instant = Date.UTC(year, month - 1, day, hour, minute);

  for (let pass = 0; pass < 2; pass += 1) {
    const seen = getZonedParts(new Date(instant), timezone);
    const seenAsUtc = Date.UTC(
      seen.year,
      seen.month - 1,
      seen.day,
      seen.hour,
      seen.minute
    );
    instant -= seenAsUtc - Date.UTC(year, month - 1, day, hour, minute);
  }

  return new Date(instant);
};

export const isWithinWorkingHours = (
  startTimes = DEFAULT_START_TIMES,
  endTimes = DEFAULT_END_TIMES,
  timezone = DEFAULT_TIMEZONE,
  activeDays = DEFAULT_ACTIVE_DAYS,
  now = new Date()
) => {
  const { hour, weekday } = getZonedParts(now, timezone);

  if (!activeDays?.[weekday]) {
    return false;
  }

  const dayStartTime = startTimes?.[weekday];
  const dayEndTime = endTimes?.[weekday];

  if (dayStartTime == null || dayEndTime == null) {
    return false;
  }

  return hour >= dayStartTime && hour < dayEndTime;
};

const DAY_NAMES = [
  'Minggu',
  'Senin',
  'Selasa',
  'Rabu',
  'Kamis',
  'Jumat',
  'Sabtu'
];

const MONTH_NAMES = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember'
];

const formatTimeString = (hour, minute = 0) =>
  `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

const formatFullDate = (date) =>
  `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;

const addDaysInZone = ({ year, month, day }, offset) => {
  const shifted = new Date(Date.UTC(year, month - 1, day + offset));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    weekday: shifted.getUTCDay()
  };
};

const isSameLocalDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Returns the next moment the schedule opens, described in the *viewer's* local
// time. The search runs entirely in the schedule's own timezone; the result is
// converted to a real instant, after which the local getters do the display
// conversion for free — no manual offset arithmetic.
export const getNextAvailableTime = (
  startTimes = DEFAULT_START_TIMES,
  timezone = DEFAULT_TIMEZONE,
  activeDays = DEFAULT_ACTIVE_DAYS,
  t = null,
  now = new Date()
) => {
  const zone = resolveTimezone(timezone);
  const today = getZonedParts(now, zone);

  for (let offset = 0; offset <= 7; offset += 1) {
    const candidate = addDaysInZone(today, offset);

    if (!activeDays?.[candidate.weekday]) {
      continue;
    }

    const startHour = startTimes?.[candidate.weekday];
    if (startHour == null) {
      continue;
    }

    // Today only counts if the opening hour has not passed yet.
    if (offset === 0 && today.hour >= startHour) {
      continue;
    }

    const dateObject = zonedWallClockToDate(
      { ...candidate, hour: startHour, minute: 0 },
      zone
    );

    const localNow = new Date(now);
    const localTomorrow = new Date(now);
    localTomorrow.setDate(localTomorrow.getDate() + 1);

    return {
      day: DAY_NAMES[dateObject.getDay()],
      time: formatTimeString(dateObject.getHours(), dateObject.getMinutes()),
      fullDate: formatFullDate(dateObject),
      dateObject,
      isToday: isSameLocalDay(dateObject, localNow),
      isTomorrow: isSameLocalDay(dateObject, localTomorrow)
    };
  }

  const notAvailableText = t ? t('unavailable') : 'Tidak tersedia';

  return {
    day: notAvailableText,
    time: '--:--',
    fullDate: notAvailableText,
    dateObject: null,
    isToday: false,
    isTomorrow: false
  };
};

export const isConsultantOutOfHours = (consultant, now = new Date()) => {
  if (
    !consultant?.activeDays ||
    !consultant?.startTimes ||
    !consultant?.endTimes
  ) {
    return true;
  }

  return !isWithinWorkingHours(
    consultant.startTimes,
    consultant.endTimes,
    consultant.timezone || DEFAULT_TIMEZONE,
    consultant.activeDays,
    now
  );
};

export const getConsultantNextAvailableTime = (consultant, t, now = new Date()) => {
  if (!consultant?.activeDays || !consultant?.startTimes) {
    return t
      ? t("scheduleNotAvailable")
      : "Schedule not available";
  }

  const nextTime = getNextAvailableTime(
    consultant.startTimes,
    consultant.timezone || DEFAULT_TIMEZONE,
    consultant.activeDays,
    t,
    now
  );

  const notAvailableText = t ? t("unavailable") : "Tidak tersedia";
  if (nextTime.day === notAvailableText) {
    return t ? t("nextTimeNotFound") : "Next available time not found";
  }

  if (nextTime.isToday) {
    const todayText = t ? t("today") : "Hari Ini";
    return `${todayText}, ${nextTime.time}`;
  } else if (nextTime.isTomorrow) {
    const tomorrowText = t ? t("tomorrow") : "Besok";
    return `${tomorrowText}, ${nextTime.time}`;
  } else {
    return `${nextTime.day}, ${nextTime.fullDate}, ${nextTime.time}`;
  }
};

// General consultation has no per-consultant schedule, so it runs every day on
// the default hours — but callers already pass a config object, so honour it
// when it carries a schedule instead of silently dropping it.
const GENERAL_ACTIVE_DAYS = [true, true, true, true, true, true, true];

const generalSchedule = (config) => ({
  startTimes: config?.startTimes || DEFAULT_START_TIMES,
  endTimes: config?.endTimes || DEFAULT_END_TIMES,
  activeDays: config?.activeDays || GENERAL_ACTIVE_DAYS,
  timezone: config?.timezone || DEFAULT_TIMEZONE
});

export const isGeneralConsultationOutOfHours = (config, now = new Date()) => {
  const { startTimes, endTimes, activeDays, timezone } =
    generalSchedule(config);

  return !isWithinWorkingHours(
    startTimes,
    endTimes,
    timezone,
    activeDays,
    now
  );
};

export const getGeneralConsultationNextAvailableTime = (
  t,
  config,
  now = new Date()
) => {
  const { startTimes, activeDays, timezone } = generalSchedule(config);

  const nextTime = getNextAvailableTime(
    startTimes,
    timezone,
    activeDays,
    t,
    now
  );

  const notAvailableText = t ? t("unavailable") : "Tidak tersedia";
  if (nextTime.day === notAvailableText) {
    return t ? t("nextTimeNotFound") : "Next available time not found";
  }

  if (nextTime.isToday) {
    const todayText = t ? t("today") : "Hari Ini";
    return `${todayText}, ${nextTime.time}`;
  }

  if (nextTime.isTomorrow) {
    const tomorrowText = t ? t("tomorrow") : "Besok";
    return `${tomorrowText}, ${nextTime.time}`;
  }

  return `${nextTime.day}, ${nextTime.fullDate}, ${nextTime.time}`;
};
